import { QueryCacheOptions } from '@fullstory/proto/fs/services/alexa';
import { Duration, duration, timestamp, Timestamp } from '@fullstory/proto/google/protobuf';

/**
 * This intermediary type narrows QueryCacheOptions keys to `duration` and not `duration | null`.
 * This is technically correct for the types defined below since `getIfAgeLessThan` and `computeIfOlderThan`
 * are NEVER null. However, the protobuf supports null so this is a safe way to cast the configs
 * below without nullability. This was ultimately added because the UI components expect these values
 * to not be null.
 */
interface QueryCacheOptionsConfig extends QueryCacheOptions {
  getIfAgeLessThan: duration;
  computeIfOlderThan: duration;
}

export const optSkipCache: QueryCacheOptionsConfig = {
  getIfAgeLessThan: Duration.fromMillis(0),
  computeIfOlderThan: Duration.fromMillis(0),
  skipCache: true,
  queue: QueryCacheOptions.Queue.QUEUE_INVALID,
};

// optRefresh is query cache options to refresh data for searchies except the
// user list's searchies. The values matches the most aggressive values that the
// server allows for searchies expcept the user list's searchies.
export const optRefresh: QueryCacheOptionsConfig = {
  getIfAgeLessThan: Duration.fromSeconds(5 * 60),
  computeIfOlderThan: Duration.fromSeconds(60),
  skipCache: false,
  queue: QueryCacheOptions.Queue.QUEUE_INVALID,
};

// optRefreshUserList is query cache options to refresh data for the user
// list's searchies, namely MatchingUsers and TimeRangeCounts.
export const optRefreshUserList: QueryCacheOptionsConfig = {
  getIfAgeLessThan: Duration.fromSeconds(60),
  computeIfOlderThan: Duration.fromSeconds(60),
  skipCache: false,
  queue: QueryCacheOptions.Queue.QUEUE_INVALID,
};

export function isDataFresh(o: QueryCacheOptions | null, computedAt: Date, now: Date): boolean {
  const d = now.getTime() - computedAt.getTime();
  return o !== null && d < Duration.toMillis(o.computeIfOlderThan);
}

export function isDataFreshProto(
  o: QueryCacheOptions | null,
  computedAt: timestamp | null,
  now: Date,
): boolean {
  if (!computedAt || !o) {
    return false;
  }
  const d = now.getTime() - Timestamp.toMillis(computedAt);
  return d < Duration.toMillis(o.computeIfOlderThan);
}
